import {
  ChangeDetectionStrategy,
  Component,
  computed,
  HostBinding,
  OnChanges,
  OnDestroy,
  OnInit,
  Signal,
  signal,
  SimpleChanges,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import isEqual from 'fast-deep-equal';
import { omit, uniqWith, zipWith } from 'lodash-es';
import { combineLatest, map, Observable } from 'rxjs';

import {
  CanvasElementDataProperties,
  getBackground,
  ImageBehavior,
  ImageRatio,
  InputElementMetaUiFeatures,
  makeFontStyleStyles,
  OptionPickerDefaultValue,
  OptionsIconSettings,
  OptionsImageSettings,
  TileSelectorModes,
  toCssString,
} from '@blaze-shared/elements-domain';
import { selectSubsetByValue } from '@blaze-shared/schema-domain';
import { DatasetRow, type ElementSchema, isDatasetLinkValue } from '@blaze-shared/schema-domain';
import { safeDetectChanges } from '@blaze-shared/shared-ui';
import { isNullish } from '@blaze-shared/shared-util';
import { FALLBACK_THEME } from '@blaze-shared/theming-domain';

import { CanvasElementComponent } from '../../canvas-elements/element-component';
import {
  filterOptionsWithEmptyLabel,
  getFirstOption,
  isListSourceBound,
  serializeBindingOutput,
} from '../../utils/canvas-element/bindings.utils';
import { CanvasElementBaseInputSelectMultipleComponent } from '../__base/base-input-select-multi.component';
import { ColumnIdOrName } from '../__base/base.component';

import { AdditionalFieldDef, CardContentConfig } from './checkbox.model';

@UntilDestroy()
@Component({
  selector: 'be-checkbox',
  templateUrl: './checkbox.component.html',
  styleUrls: ['./checkbox.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CheckboxComponent
  extends CanvasElementBaseInputSelectMultipleComponent<'checkbox'>
  implements CanvasElementComponent<'checkbox'>, OnChanges, OnInit, OnDestroy
{
  @HostBinding('style.--b-col-bool-on-bg') colBoolOnBg = FALLBACK_THEME.value.accent;
  @HostBinding('style.--b-col-bool-on-mark') colBoolOnMark = FALLBACK_THEME.value.fill;

  protected readonly cardStyles$ = this.props$.pipe(
    map((props) => {
      const cardStyle = props?.style?.card;
      if (!cardStyle) {
        return undefined;
      }
      return {
        heading: {
          align: cardStyle.heading?.align,
        },
        subheading: {
          color: cardStyle.subheading?.color?.color1,
          fontWeight: cardStyle.subheading?.fontWeight,
          fontSize: cardStyle.subheading?.fontSize,
          fontStyle: cardStyle.subheading?.fontStyle,
          spaceAbove: cardStyle.subheading?.spaceAbove,
          align: cardStyle.subheading?.align,
        },
        description: {
          color: cardStyle.description?.color?.color1,
          fontSize: cardStyle.description?.fontSize,
          fontWeight: cardStyle.description?.fontWeight,
          spaceAbove: cardStyle.description?.spaceAbove,
          align: cardStyle.description?.align,
        },
        customField: {
          color: cardStyle.customField?.color?.color1,
          fontSize: cardStyle.customField?.fontSize,
          spaceAbove: cardStyle.customField?.spaceAbove,
          align: cardStyle.customField?.align,
        },
      };
    }),
  );

  protected readonly cardContentForOption: Signal<(option: DatasetRow) => CardContentConfig>;

  readonly elementKeyRoot = 'b-ch';
  elementKey!: string;

  labelEnabled!: boolean;
  labelText!: string;
  defaultValue: OptionPickerDefaultValue = 'none';
  isReadonly = false;

  styles!: Record<string, string>;
  inputWrapperStyles!: Record<string, string>;
  inputStyles!: Record<string, string>;
  tileStyle: Record<string, string>;
  tileStyles: Record<string, string>;
  labelStyles: Record<string, string>;
  subTextStyles: Record<string, string> | undefined;
  maxLayoutColumn!: Record<string, string>;
  validationStyles!: Record<string, string>;
  colBoolLabel!: string;

  displayedOptions: DatasetRow[] = [];
  mode: TileSelectorModes;
  ratio: ImageRatio = 'none';
  imageLabelPosition: 'left' | 'top' | 'bottom' | 'right' = 'left';
  layout = 'row';
  imageBehavior: ImageBehavior = 'contain';
  cardAlign: 'left' | 'right' | 'center' | undefined;
  iconSettings: OptionsIconSettings | undefined;
  imageSettings: OptionsImageSettings | undefined;
  showCheckIcon: boolean | undefined;

  additionalFieldDefs = signal<AdditionalFieldDef[]>([]);
  visibleAdditionalFieldDefs$: Observable<AdditionalFieldDef[] | undefined>;

  @ColumnIdOrName() subheadingField = '';
  @ColumnIdOrName() descriptionField = '';

  get hasValidationError(): boolean | undefined {
    return this.formState?.errors?.required || !!this.formState?.errors?.invalid;
  }

  get selectedItems(): DatasetRow[] {
    return this.selection.selected;
  }

  get showCheckmark(): boolean {
    return !!this.showCheckIcon && (this.mode === 'box' || this.mode === 'card');
  }

  get boxSubTextField(): string {
    return this.bindingResults?.['optionsList'] ? this.subheadingField : 'subheading';
  }

  get showIcon(): boolean {
    return this.displayedOptions.some((option) => !!option.icon);
  }

  get showImage(): boolean {
    const ab = this.bindingResults?.['optionsList'];
    return !ab || (!!ab && !!this.imageField);
  }

  constructor() {
    super();
    this.initMarginHandler((props) => props?.style?.general?.offset?.margin);

    this.visibleAdditionalFieldDefs$ = combineLatest({
      context: this.runtimeContext$,
      customFields: toObservable(this.additionalFieldDefs).pipe(
        map((fields) => fields.filter((field) => field.fieldKey)),
      ),
    }).pipe(
      map(({ context, customFields }) => {
        if (!customFields) {
          return;
        }

        const roleFiltered = customFields.some((field) => !!field.visible?.accessRoles?.length)
          ? customFields.filter(
              (field) =>
                !field.visible?.accessRoles?.length ||
                this.userAccessRolesService.isCurrentUserInAccessRoles(field.visible.accessRoles),
            )
          : customFields;

        // If no custom fields are conditionally visible - show the same custom fields for all rows
        if (!roleFiltered.some((field) => !!field.visibilityExpression)) {
          return roleFiltered;
        }

        // Normalization is done here for improved performance - the normalized context is shared across all rows
        const normalizedContext = this.normalizeLinkedParams(context);

        return roleFiltered.filter(
          (field) =>
            !field.visibilityExpression ||
            !!this.evaluateExpression(field.visibilityExpression, normalizedContext, true),
        );
      }),
    );
    const visibleDefsSignal = toSignal(this.visibleAdditionalFieldDefs$, {
      initialValue: [] as AdditionalFieldDef[],
    });
    this.cardContentForOption = computed(() => {
      const visibleDefs = visibleDefsSignal();
      return (option: DatasetRow): CardContentConfig => ({
        subheading: option[this.subheadingField],
        description: option[this.descriptionField],
        customFields: visibleDefs?.map((def) => ({ ...def, value: option[def.fieldKey] })),
      });
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.['props']) {
      if (!this.elementKey) {
        this.elementKey = `${this.elementKeyRoot}-${this.elementId}`;
      }

      const {
        title,
        checkbox,
        mode,
        image: imageSettings,
        icon: iconSettings,
        showCheckIcon,
      } = this.props?.content || {};
      this.mode = mode ?? 'text';

      this.iconSettings = iconSettings;
      this.imageSettings = imageSettings;
      this.showCheckIcon = showCheckIcon;

      this.labelEnabled = title?.enabled ?? true;

      this.processBindingOptionsField(checkbox?.values);

      this.assignAdditionalFields();

      const hasBoundOptionList = this.bindingResults?.['optionsList'];
      if (['image', 'box'].includes(this.mode) && !hasBoundOptionList) {
        this.optionsList = zipWith(
          this.optionsList ?? [],
          checkbox?.images ?? [],
          checkbox?.subheading ?? [],
          (option, image, subheading) => ({
            ...option,
            imageSrc: image?.src,
            icon: image?.icon,
            ...(this.mode === 'box' && subheading ? { subheading } : {}),
          }),
        );
      } else if (this.mode === 'card' && !hasBoundOptionList) {
        this.optionsList = zipWith(
          this.optionsList ?? [],
          checkbox?.images ?? [],
          checkbox?.subheading ?? [],
          checkbox?.description ?? [],
          (option, image, subheading, description) => ({
            ...option,
            imageSrc: image?.src,
            subheading,
            description,
          }),
        );
      }
      this.updateStyles();

      this.ratio = imageSettings?.ratio ?? 'none';
      this.imageBehavior = imageSettings?.behavior ?? 'cover';
      this.mode = mode ?? 'text';
      this.layout = checkbox?.layout ?? 'row';

      if (this.mode === 'image') {
        this.imageLabelPosition = imageSettings?.position ?? 'left';
      } else if (this.mode === 'card') {
        this.imageLabelPosition = imageSettings?.position ?? 'right';
      } else {
        this.imageLabelPosition = iconSettings?.position ?? 'left';
      }

      this.cardAlign = checkbox?.cardAlign;
    }

    if (changes?.['props'] || changes?.['selected'] || changes?.['optionsList']) {
      this.updateOptionsList();
      this.updateSelection();
    }

    if (changes?.['props']) {
      this.isSourceBound = isListSourceBound(this.props?.data as CanvasElementDataProperties[]);
    }
  }

  ngOnInit(): void {
    this.getElementUiFeatures<InputElementMetaUiFeatures>()
      .pipe(untilDestroyed(this))
      .subscribe((features) => {
        this.labelText = (features.title ?? '') + this.getRequiredSuffix(features);

        this.isReadonly = features.isReadOnly || this.props?.content?.checkbox?.readonly || false;

        safeDetectChanges(this.cdr);
      });

    this.stylesCleanup.register(this.elementKey);
  }

  ngOnDestroy(): void {
    this.stylesCleanup.dispose(this.elementKey);
  }

  reset(): void {
    this.outputEmitTracker.reset();
    this.updateSelection();
    this.cdr.detectChanges();
  }

  datasetCompareBy(row: DatasetRow): any {
    return JSON.stringify(row);
  }

  onSelectChange(options: DatasetRow[] | undefined): void {
    if (!options || this.isReadonly) {
      return;
    }

    this.selection.clear();
    options.forEach((option) => {
      this.selection.select(option);
    });
  }

  protected override filterOptions(): void {
    /** */
  }

  protected override applyExtraBindingFields(bindings: Record<string, unknown>): void {
    this.subheadingField = typeof bindings['subheading'] === 'string' ? bindings['subheading'] : '';
    this.descriptionField =
      typeof bindings['description'] === 'string' ? bindings['description'] : '';
  }

  protected override updateOutput(data?: any[] | null): void {
    if (!data) {
      data = [];
    }
    this.updateSelectionModel(data);
    const schema = this.getEventSchema('change');

    const cleanData = this.adjustWithOutputSchema(data, schema);
    const serialized = serializeBindingOutput(
      this.props,
      cleanData,
      this.valueField,
      this.labelField,
    );
    const isInitialData = !this.outputEmitTracker.hasPrevious();

    if (!this.outputEmitTracker.shouldEmit(serialized)) {
      return;
    }

    this.selected = serialized;

    this.output.emit({
      eventType: 'change',
      data: serialized,
      schema,
      isInitialData,
    });
  }

  private updateOptionsList(): void {
    this.displayedOptions = [...(this.optionsList || [])].filter(
      filterOptionsWithEmptyLabel(this.labelField),
    );

    // Keep unique value-label pairs (value can be a nested object)
    this.displayedOptions = uniqWith(
      this.displayedOptions,
      (a: DatasetRow, b: DatasetRow) =>
        (isEqual(a[this.valueField], b[this.valueField]) &&
          a[this.labelField] === b[this.labelField]) ||
        (isDatasetLinkValue(a[this.labelField]) &&
          isDatasetLinkValue(b[this.labelField]) &&
          a[this.labelField].rowId === b[this.labelField].rowId),
    );
  }

  private updateSelection(): void {
    const { checkbox, data } = this.props?.content || {};

    const wasSourceBound = this.isSourceBound;
    const isSourceBound = isListSourceBound(this.props?.data as CanvasElementDataProperties[]);
    if (wasSourceBound !== undefined && wasSourceBound !== isSourceBound) {
      this.selectedValue = undefined;
    }
    this.defaultValue = data?.defaultValue || 'none';

    let selected = null;
    if (
      this.optionsList?.length &&
      !isNullish(this.selectedValue) &&
      (!Array.isArray(this.selectedValue) || this.selectedValue?.length)
    ) {
      selected = selectSubsetByValue(
        this.optionsList,
        Array.isArray(this.selectedValue) ? this.selectedValue : [this.selectedValue],
        this.valueField,
      );
    } else {
      switch (this.defaultValue) {
        case 'first':
          selected = [getFirstOption(this.optionsList, this.valueField)];
          break;
        case 'fixedValue':
          if (checkbox?.preselect) {
            selected = selectSubsetByValue(this.optionsList, checkbox?.preselect, this.valueField);
          }
          break;
      }
    }

    if (this.optionsList?.length) {
      this.updateOutput(selected);
    }
  }

  private adjustWithOutputSchema(
    data: DatasetRow[],
    schema: ElementSchema | undefined,
  ): DatasetRow[] {
    return data.map((item) => {
      const defaultSchema = this.getDefaultEventSchema('change');
      if (!schema) {
        return item;
      }
      const isDefaultSchema = schema.columns === defaultSchema?.columns;
      if (!isDefaultSchema) {
        return item;
      }
      return omit(item, [this.imageField, this.iconField]);
    });
  }

  private updateStyles(): void {
    const { style, content } = this.props || {};
    const { title, checkbox: checkContent } = content || {};
    const { general, checkbox, labelBlock } = style || {};
    if (!style || !title || !general || !checkbox || !labelBlock) {
      return;
    }

    const radioBorderBackground = getBackground(checkbox.border);
    const styles: Record<string, string> = {
      '--radio-accent': general.accent.color1,
      '--radio-background': getBackground(checkbox.fill),
      '--b-col-bool-on-bg': general.accent.color1,
      '--radio-card-checkmark-border':
        radioBorderBackground === 'none' ? 'transparent' : radioBorderBackground,
      '--radio-card-checkmark-tick': checkbox.label.color1,
      '--radio-chip-fill': getBackground(checkbox.fill),
      '--tile-padding': checkbox.padding ?? '16px',
      '--tile-gap': checkbox.gap ?? '8px',
    };
    if (checkbox.tileRadius) {
      styles['--tile-radius'] = checkbox.tileRadius;
    }
    if (checkbox.checkmarkSize) {
      styles['--checkmark-size'] = checkbox.checkmarkSize;
    }

    const inputWrapperStyles: Record<string, string> = {};
    const inputStyles: Record<string, string> = {};
    const tileStyle: Record<string, string> = {};
    const tileStyles: Record<string, string> = {};
    const labelStyles: Record<string, string> = {};
    const validationStyles: Record<string, string> = {};
    const maxLayoutColumn: Record<string, string> = {};

    if (title.enabled !== false) {
      if (title.position === 'top') {
        styles['flex-direction'] = `column`;
        styles['align-items'] = `flex-start`;
      } else {
        validationStyles['marginLeft'] = title.width;
        validationStyles['paddingLeft'] = '8px';
        inputWrapperStyles['flexBasis'] = `${100 - parseInt(title.width, 10)}%`;
      }
    }

    labelStyles['fontSize'] = style.fontSize;
    labelStyles['fontFamily'] = style.fontFamily;
    labelStyles['fontWeight'] = style.fontWeight;
    labelStyles['lineHeight'] = 'normal';
    labelStyles['color'] = style.checkbox.label.color1;
    if (checkbox.fill) {
      tileStyles['background'] = getBackground(checkbox.fill);
    }
    if (checkbox.border) {
      tileStyles['borderColor'] =
        checkbox.border.selectorType === 'none' ? 'transparent' : checkbox.border.color1;
    }

    const fontStyles = makeFontStyleStyles(style.fontStyle);
    Object.assign(labelStyles, fontStyles);

    if (checkContent?.layout === 'column') {
      const colSpacing =
        this.isUsingOldPaddingStrategy || this.elementContext?.extendInputPadding ? '8px' : '16px';
      inputStyles['flexDirection'] = 'row';
      maxLayoutColumn['flex'] =
        `0 0 calc(${Math.floor(100 / Number(checkContent.maxColumns?.value ?? 1))}% - ${colSpacing})`;
      maxLayoutColumn['minWidth'] = `${checkContent.minWidth?.value ?? 1}px`;
      maxLayoutColumn['width'] = `max-content`;
      maxLayoutColumn['maxWidth'] = `100%`;

      inputStyles['display'] = 'grid';
      inputStyles['gridTemplateColumns'] =
        `repeat(auto-fill, minmax(min(100%, max(${checkContent.minWidth?.value ?? 1}px,
                                                            100%/${Number(checkContent.maxColumns?.value ?? 1) + 1})), 1fr))`;
    }
    if (!(this.isUsingOldPaddingStrategy || this.elementContext?.extendInputPadding)) {
      inputStyles['rowGap'] = '8px';
      inputStyles['columnGap'] = '16px';
    }

    this.styles = styles;
    this.inputWrapperStyles = inputWrapperStyles;
    this.inputStyles = inputStyles;
    this.tileStyle = tileStyle;
    this.tileStyles = tileStyles;
    this.labelStyles = labelStyles;

    const subText = style.box?.subText;
    this.subTextStyles = subText
      ? {
          color: subText.color?.color1 ?? '',
          fontFamily: subText.fontFamily ?? '',
          fontSize: subText.fontSize ?? '',
          lineHeight: subText.lineHeight ?? '',
        }
      : undefined;
    this.maxLayoutColumn = maxLayoutColumn;
    this.validationStyles = validationStyles;

    this.colBoolOnBg = checkbox.fill.color1;
    this.colBoolOnMark = checkbox.symbol.color1;
    this.colBoolLabel = checkbox.label.color1;

    this.stylesCleanup.update(
      this.elementKey,
      `
      blaze-checkbox.${this.elementKey} .blaze-checkbox-label {
        color: ${checkbox.label.color1};
        font-size: ${style.fontSize};
        font-family: ${style.fontFamily};
        line-height: ${style.lineHeight};
        font-weight: ${style.fontWeight};
        ${style.fontStyle ? toCssString(makeFontStyleStyles(style.fontStyle)) : ''}
      }
      blaze-checkbox.${this.elementKey}.bat-checkbox input[type='checkbox'] {
        background-color: ${checkbox.fill.color1};
        border-radius: ${checkbox.radius};
        border: 1px solid ${checkbox.border.selectorType === 'none' ? 'transparent' : checkbox.border.color1};
      }
      blaze-checkbox.${this.elementKey}.bat-checkbox input[type='checkbox']:checked {
        background-color: ${general.accent.color1};
        border-color: ${general.accent.color1};
      }
      blaze-checkbox.${this.elementKey}.bat-checkbox .blaze-checkbox-check-mark {
        color: ${checkbox.symbol.color1};
      }
      .${this.elementKey} mat-icon.col-boolean.checked svg {
        --b-col-bool-on-bg: ${general.accent.color1};
      }
      .${this.elementKey} .card .card-check-mark {
        background-color: ${checkbox.fill.color1};
        border: 1px solid ${checkbox.border.selectorType === 'none' ? 'transparent' : checkbox.border.color1};
      }
    `,
    );
  }

  private assignAdditionalFields(): void {
    const fields = this.props?.content?.customFields ?? [];
    this.additionalFieldDefs.set(
      fields.map((f: any) => ({
        ...f,
        label: f.label ?? '',
        fieldKey: this.getColumnName(f.column) ?? f.column ?? '',
        format: f.displayFormat ?? '',
        labelBehavior: f.labelBehavior ?? 'left',
      })),
    );
  }
}
